"use client";
import React, { useRef, useState, useEffect, useLayoutEffect, useCallback } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";

export default function OfferSlider({
  children,
  sectionId = "default",
}: {
  children: React.ReactNode;
  sectionId?: string;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  // isMobile: default true so SSR/hydration doesn't flash wrong button state on mobile
  const [isMobile, setIsMobile] = useState(true);
  const autoPlayRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Track a manual scroll index for reliable button navigation
  const currentIndexRef = useRef(0);

  const slides = React.Children.toArray(children);
  const total = slides.length;

  // Sync isMobile after mount using useLayoutEffect (runs before paint)
  useLayoutEffect(() => {
    const check = () => setIsMobile(window.matchMedia("(max-width: 639px)").matches);
    check();
    const mq = window.matchMedia("(max-width: 639px)");
    mq.addEventListener("change", check);
    return () => mq.removeEventListener("change", check);
  }, []);

  // Mobile: show buttons when >1 card; Desktop: show buttons when >4 cards
  const showButtons = isMobile ? total > 1 : total > 4;

  // Scroll to a specific index (0-based)
  const scrollToIndex = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;

    // Clamp index
    const clamped = Math.max(0, Math.min(index, total - 1));
    currentIndexRef.current = clamped;

    const firstCard = track.children[clamped] as HTMLElement | undefined;
    if (!firstCard) return;

    track.scrollTo({ left: firstCard.offsetLeft, behavior: "smooth" });
  }, [total]);

  const prev = useCallback(() => {
    const newIndex = currentIndexRef.current <= 0
      ? total - 1  // wraparound to last
      : currentIndexRef.current - 1;
    scrollToIndex(newIndex);
  }, [scrollToIndex, total]);

  const next = useCallback(() => {
    const newIndex = currentIndexRef.current >= total - 1
      ? 0  // wraparound to first
      : currentIndexRef.current + 1;
    scrollToIndex(newIndex);
  }, [scrollToIndex, total]);

  // Auto-play
  useEffect(() => {
    if (!showButtons) return;
    if (isHovered) {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
      return;
    }
    autoPlayRef.current = setInterval(() => next(), 4000);
    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };
  }, [isHovered, next, showButtons]);

  // Reset index when showButtons changes (e.g. resize)
  useEffect(() => {
    currentIndexRef.current = 0;
    if (trackRef.current) {
      trackRef.current.scrollTo({ left: 0, behavior: "instant" as ScrollBehavior });
    }
  }, [showButtons]);

  return (
    <div
      className="relative w-full"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Inner wrapper shrinks to leave room for arrow buttons */}
      <div
        className={showButtons ? "relative mx-8" : "relative"}
        style={showButtons ? { width: "calc(100% - 4rem)" } : {}}
      >
        {/* Left Arrow */}
        {showButtons && (
          <button
            onClick={prev}
            aria-label="Previous"
            className="absolute -left-10 top-1/2 -translate-y-1/2 z-20
                       w-9 h-9 flex items-center justify-center
                       bg-white border border-indigo-800 text-indigo-800 rounded-full
                       shadow-md hover:bg-blue-600 hover:border-blue-600 hover:text-white
                       hover:scale-110 transition-all duration-300 focus:outline-none"
          >
            <ArrowLeft size={16} strokeWidth={2.5} />
          </button>
        )}

        {/* Right Arrow */}
        {showButtons && (
          <button
            onClick={next}
            aria-label="Next"
            className="absolute -right-10 top-1/2 -translate-y-1/2 z-20
                       w-9 h-9 flex items-center justify-center
                       bg-white border border-indigo-800 text-indigo-800 rounded-full
                       shadow-md hover:bg-blue-600 hover:border-blue-600 hover:text-white
                       hover:scale-110 transition-all duration-300 focus:outline-none"
          >
            <ArrowRight size={16} strokeWidth={2.5} />
          </button>
        )}

        {/* Scrollable Track */}
        <div
          ref={trackRef}
          id={`offer-slider-track-${sectionId}`}
          className={`flex gap-3 md:gap-4 overflow-x-auto pb-4 pt-2 ${
            !showButtons ? "justify-center" : ""
          }`}
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            scrollSnapType: showButtons ? "x mandatory" : "none",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <style>{`#offer-slider-track-${sectionId}::-webkit-scrollbar { display: none; }`}</style>
          {slides.map((child, i) => (
            <div
              key={i}
              className="flex-shrink-0 w-full sm:w-[46%] md:w-[31%] lg:w-[calc(25%-12px)] xl:w-[calc(25%-12px)]"
              style={{ scrollSnapAlign: showButtons ? "start" : "none" }}
            >
              {child}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
